/**
 * IBasicDataFeed implementation for Bullrun.
 *
 * One Datafeed instance per mounted widget (the React wrapper constructs it in
 * its mount effect and calls `destroy()` on unmount — TV has no teardown hook
 * for the datafeed, so the wrapper owns that lifecycle).
 * Symbol prefix dispatch (`token:`, `perp:`, `pred:`, `inline:`) routes both
 * `getBars` and `subscribeBars` to the right Apollo call — there are no
 * `if` ladders elsewhere, and adding a future kind is a compile-time error
 * until the switches handle it.
 *
 * `inline:` is the mobile-on-web bridge: the wrapper component has already
 * fetched bars and hands them to the registry; `getBars` reads them back
 * synchronously and `subscribeBars` is a no-op (the inline path has no
 * realtime stream today).
 */
import { notEmpty } from "@orb-labs/types";
import { BIGINT_SCALE_FACTOR } from "../../bullrun/chartUtils.js";
import { InstrumentType } from "../../graphql/generated.js";
import { GetChartBarsQuery } from "../../graphql/queries.js";
import { InstrumentLifecycleTransitionSubscription, InstrumentPriceUpdatesSubscription, PerpTradeUpdatesSubscription, PredictionTradeUpdatesSubscription, } from "../../graphql/subscriptions.js";
import { rawProbabilityToPercent } from "../../bullrun/predictionChartSeries.js";
import { evictSymbol, getCachedBars, makeBarCacheKey, setCachedBars } from "./barCache.js";
import { resolutionToBarDurationMs, BULLRUN_TV_SUPPORTED_RESOLUTIONS, TOKEN_SUPPORTED_RESOLUTIONS, PREDICTION_SUPPORTED_RESOLUTIONS, } from "./resolution.js";
import { getSymbolMeta } from "./symbolRegistry.js";
import { TVSymbolKind, parseTVSymbol } from "./symbol.js";
import { filterBarsToWindow, perpChartDataToBars, predictionPricePointsToBars, tokenChartDataToBars, } from "./transforms.js";
const TOKEN_DEFAULT_PRICESCALE = 100_000_000;
const PRED_PRICESCALE = 1000;
const CONFIGURATION = {
    exchanges: [],
    symbols_types: [{ name: "All", value: "" }],
    supported_resolutions: BULLRUN_TV_SUPPORTED_RESOLUTIONS,
    supports_marks: false,
    supports_timescale_marks: false,
    supports_time: true,
};
export function createTVDatafeed({ client }) {
    const liveBars = new Map();
    const symbolResetCallbacks = new Map();
    // Most-recent historical bar per `${symbolId}|${resolution}`. `subscribeBars`
    // seeds the live bar from this so the first realtime tick *extends* the last
    // historical bar instead of resetting it, and snaps live ticks to the same
    // bar grid the backend used — critical for W/M resolutions where flooring
    // from the unix epoch lands on the wrong boundary (epoch is a Thursday; a
    // fixed 30-day "month" never aligns to calendar months).
    const lastBarByKey = new Map();
    // Symbols whose backing data changed under us (delist/resolution/settlement)
    // and whose next `getBars` must bypass the Apollo cache. Clearing only the
    // module bar-cache is not enough: Apollo's `cache-first` would re-serve the
    // pre-transition response and the "reset" would show stale history.
    const symbolsNeedingRevalidation = new Set();
    // Lifecycle-transition subscription must cover the cache surface, not the
    // live-subscription surface — an inline-mode or getBars-only symbol that is
    // never `subscribeBars`-ed still has rows in the LRU that go stale on a
    // delist/resolution. Start the listener eagerly with the datafeed itself.
    const lifecycleSubscription = client.subscribe({ query: InstrumentLifecycleTransitionSubscription }).subscribe({
        next: (result) => {
            const stiId = result.data?.instrumentLifecycleTransition?.standardizedInstrumentId;
            if (!notEmpty(stiId))
                return;
            // A single instrument id can back token/perp/pred symbols (and any
            // number of `pred:.../<outcome>` flavors). Match on prefix.
            const affected = [];
            for (const key of symbolResetCallbacks.keys()) {
                if (key.endsWith(`:${stiId}`) || key.includes(`:${stiId}/`))
                    affected.push(key);
            }
            for (const symbolId of affected) {
                evictSymbol(symbolId);
                symbolsNeedingRevalidation.add(symbolId);
                clearLastBars(lastBarByKey, symbolId);
                const handlers = symbolResetCallbacks.get(symbolId);
                if (!handlers)
                    continue;
                for (const handler of handlers)
                    handler();
            }
        },
    });
    return {
        onReady(callback) {
            // TV requires asynchronous push; the docs are explicit about this and
            // a synchronous call deadlocks the chart on init.
            setTimeout(() => callback(CONFIGURATION), 0);
        },
        searchSymbols(_userInput, _exchange, _symbolType, onResult) {
            // The TV symbol-search UI is disabled (`header_symbol_search` in
            // features.ts) — symbols are always set programmatically.
            onResult([]);
        },
        resolveSymbol(symbolName, onResolve, onError) {
            let parsed;
            try {
                parsed = parseTVSymbol(symbolName);
            }
            catch {
                onError("invalid_symbol");
                return;
            }
            const meta = getSymbolMeta(symbolName);
            if (!meta) {
                // The widget wrapper guarantees `registerSymbol` runs before the
                // widget is constructed; reaching here means a programming error
                // (e.g. setSymbol called on a symbolId that was never registered).
                onError("symbol_not_found");
                return;
            }
            const info = buildLibrarySymbolInfo(symbolName, parsed, meta);
            // TV expects async resolution.
            setTimeout(() => onResolve(info), 0);
        },
        getBars(symbolInfo, resolution, periodParams, onResult, onError) {
            const symbolId = symbolInfo.ticker;
            let parsed;
            try {
                parsed = parseTVSymbol(symbolId);
            }
            catch (error) {
                onError(`invalid_symbol:${error.message}`);
                return;
            }
            const cacheKey = makeBarCacheKey(symbolId, resolution, periodParams.from, periodParams.to);
            const cached = getCachedBars(cacheKey);
            if (cached) {
                onResult(cached, { noData: cached.length === 0 });
                return;
            }
            // A pending revalidation (set by a lifecycle transition) bypasses the
            // Apollo cache exactly once, then clears — subsequent windows in the same
            // reset read the now-fresh Apollo cache via `cache-first`.
            const forceNetwork = symbolsNeedingRevalidation.delete(symbolId);
            fetchBars(client, parsed, resolution, periodParams, forceNetwork)
                .then((bars) => {
                const windowed = filterBarsToWindow(bars, periodParams.from, periodParams.to);
                setCachedBars(symbolId, cacheKey, windowed);
                rememberLastBar(lastBarByKey, symbolId, resolution, windowed);
                onResult(windowed, { noData: windowed.length === 0 });
            })
                .catch((error) => {
                // Surface a useful diagnostic — TV's error UI shows the reason string.
                onError(`getBars_failed:${error.message ?? "unknown"}`);
            });
        },
        subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onResetCacheNeededCallback) {
            const symbolId = symbolInfo.ticker;
            let parsed;
            try {
                parsed = parseTVSymbol(symbolId);
            }
            catch {
                return;
            }
            const resetHandlers = symbolResetCallbacks.get(symbolId) ?? new Set();
            resetHandlers.add(onResetCacheNeededCallback);
            symbolResetCallbacks.set(symbolId, resetHandlers);
            if (parsed.kind === TVSymbolKind.INLINE) {
                // Inline mode has no realtime feed; nothing to subscribe to. Reset
                // handler stays registered so a lifecycle event still triggers a
                // refetch via the next `getBars`.
                liveBars.set(listenerGuid, {
                    apolloSubscription: { unsubscribe: () => undefined },
                    resolution,
                    onTick,
                    symbolId,
                    onResetCacheNeededCallback,
                });
                return;
            }
            const running = {
                apolloSubscription: { unsubscribe: () => undefined },
                // Seed from the latest historical bar (if `getBars` has already run) so
                // the first live tick merges into it rather than overwriting its OHLC,
                // and so the bar grid below is phased to the backend's boundaries.
                currentBar: cloneBar(lastBarByKey.get(`${symbolId}|${resolution}`)),
                resolution,
                onTick,
                symbolId,
                onResetCacheNeededCallback,
            };
            liveBars.set(listenerGuid, running);
            const barDurationMs = resolutionToBarDurationMs(resolution);
            const isPrediction = parsed.kind === TVSymbolKind.PRED;
            const isToken = parsed.kind === TVSymbolKind.TOKEN;
            const handleTick = (price, volume, timestampMs) => {
                // Snap relative to the current bar's open time so the live grid keeps
                // the same phase as the historical bars (which are not epoch-aligned at
                // W/M). With no anchor yet, fall back to epoch-floor.
                const anchor = running.currentBar?.time;
                const barTime = notEmpty(anchor)
                    ? anchor + Math.floor((timestampMs - anchor) / barDurationMs) * barDurationMs
                    : Math.floor(timestampMs / barDurationMs) * barDurationMs;
                // Drop out-of-order ticks that fall before the current bar — TV rejects
                // bars that move time backwards, and merging a stale price would corrupt
                // the bar we seeded from history.
                if (running.currentBar && barTime < running.currentBar.time)
                    return;
                if (!running.currentBar || barTime > running.currentBar.time) {
                    // Roll: emit the closed bar (if any), open a new one starting at the tick.
                    if (running.currentBar)
                        onTick(running.currentBar);
                    running.currentBar = {
                        time: barTime,
                        open: price,
                        high: price,
                        low: price,
                        close: price,
                        volume: isPrediction || isToken ? 0 : volume,
                    };
                    onTick(running.currentBar);
                    return;
                }
                const bar = running.currentBar;
                bar.close = price;
                if (price > bar.high)
                    bar.high = price;
                if (price < bar.low)
                    bar.low = price;
                if (isToken) {
                    // Token price updates don't carry trade size — match the historical
                    // synthetic-volume formula in tokenChartSeries (`|close-open| * 1000`)
                    // so the rightmost live bar's histogram is continuous with the
                    // historical bars to its left.
                    bar.volume = Math.abs(bar.close - bar.open) * 1000;
                }
                else if (!isPrediction) {
                    bar.volume = (bar.volume ?? 0) + volume;
                }
                onTick(bar);
            };
            running.apolloSubscription = startApolloSubscription(client, parsed, handleTick);
        },
        unsubscribeBars(listenerGuid) {
            const running = liveBars.get(listenerGuid);
            if (!running)
                return;
            running.apolloSubscription.unsubscribe();
            // Drop the lifecycle handler too — otherwise a transition arriving
            // after the widget is torn down would call into the dead closure.
            const handlers = symbolResetCallbacks.get(running.symbolId);
            if (handlers) {
                handlers.delete(running.onResetCacheNeededCallback);
                if (handlers.size === 0)
                    symbolResetCallbacks.delete(running.symbolId);
            }
            liveBars.delete(listenerGuid);
        },
        destroy() {
            // TV never calls this — the React wrapper invokes it on unmount. Tear
            // down the datafeed-level lifecycle stream (started in this closure and
            // otherwise leaked, one per chart mount) plus any live bar subscriptions
            // that outlived their `unsubscribeBars`.
            lifecycleSubscription.unsubscribe();
            for (const running of liveBars.values())
                running.apolloSubscription.unsubscribe();
            liveBars.clear();
            symbolResetCallbacks.clear();
            symbolsNeedingRevalidation.clear();
            lastBarByKey.clear();
        },
    };
}
function buildFullName(kind, meta) {
    if (kind === TVSymbolKind.PERP)
        return `${meta.displaySymbol}-PERP`;
    if (kind === TVSymbolKind.PRED) {
        return `${meta.marketTitle ?? meta.displaySymbol} — ${meta.outcomeLabel ?? meta.displaySymbol}`;
    }
    return meta.displaySymbol;
}
function buildLibrarySymbolInfo(ticker, parsed, meta) {
    const isPrediction = parsed.kind === TVSymbolKind.PRED;
    const isInline = parsed.kind === TVSymbolKind.INLINE;
    // Only perps (Hyperliquid) serve W/M and arbitrary range; token/prediction
    // map resolution → a fixed ChartTimeframe whose bars top out at daily.
    const isPerp = parsed.kind === TVSymbolKind.PERP;
    const supportedResolutions = isPerp
        ? BULLRUN_TV_SUPPORTED_RESOLUTIONS
        : isPrediction
            ? PREDICTION_SUPPORTED_RESOLUTIONS
            : TOKEN_SUPPORTED_RESOLUTIONS;
    return {
        ticker,
        name: meta.displaySymbol,
        full_name: buildFullName(parsed.kind, meta),
        description: meta.marketTitle ?? meta.displaySymbol,
        type: isPrediction ? "index" : "crypto",
        session: "24x7",
        timezone: "Etc/UTC",
        exchange: "",
        listed_exchange: "",
        format: "price",
        pricescale: meta.pricescale,
        minmov: 1,
        has_intraday: !isInline,
        has_seconds: false,
        has_daily: !isInline,
        // Only perps have weekly/monthly bars; token/prediction configs top out at
        // daily, so W/M would surface toolbar buttons that return no data.
        has_weekly_and_monthly: isPerp,
        supported_resolutions: supportedResolutions,
        visible_plots_set: isPrediction ? "c" : "ohlcv",
        volume_precision: 0,
        data_status: "streaming",
    };
}
async function fetchBars(client, parsed, resolution, periodParams, forceNetwork) {
    // After a lifecycle transition the cached GraphQL response is stale; go to
    // the network once to repopulate Apollo's cache with post-transition data.
    const fetchPolicy = forceNetwork ? "network-only" : "cache-first";
    switch (parsed.kind) {
        case TVSymbolKind.INLINE: {
            // Inline mode reads pre-fetched bars from the registry — no GraphQL call.
            const meta = getSymbolMeta(`${TVSymbolKind.INLINE}:${parsed.handle}`);
            return meta?.inlineBars ?? [];
        }
        case TVSymbolKind.TOKEN: {
            const result = await client.query({
                query: GetChartBarsQuery,
                variables: {
                    input: {
                        standardizedInstrumentId: parsed.standardizedInstrumentId,
                        instrumentType: InstrumentType.FungibleToken,
                        resolution,
                        fromSec: periodParams.from,
                        toSec: periodParams.to,
                        countBack: periodParams.countBack,
                    },
                },
                fetchPolicy,
            });
            return tokenChartDataToBars(result.data?.getChartBars?.data);
        }
        case TVSymbolKind.PERP: {
            // All three kinds use the resolution+range `getChartBars` path: TV
            // `resolution` is the bar size and the window is driven by `periodParams`,
            // so the chart gets correctly-grained bars over the visible range (and can
            // paginate) instead of a fixed timeframe bundle. Returns the same
            // MarketDataTimeSeriesAndSummary shape, so the transforms are unchanged.
            const result = await client.query({
                query: GetChartBarsQuery,
                variables: {
                    input: {
                        standardizedInstrumentId: parsed.standardizedInstrumentId,
                        instrumentType: InstrumentType.PerpetualContract,
                        resolution,
                        fromSec: periodParams.from,
                        toSec: periodParams.to,
                        countBack: periodParams.countBack,
                    },
                },
                fetchPolicy,
            });
            return perpChartDataToBars(result.data?.getChartBars?.data);
        }
        case TVSymbolKind.PRED: {
            const result = await client.query({
                query: GetChartBarsQuery,
                variables: {
                    input: {
                        standardizedInstrumentId: parsed.standardizedInstrumentId,
                        instrumentType: InstrumentType.PredictionMarket,
                        outcomeTokenId: parsed.outcomeTokenId,
                        resolution,
                        fromSec: periodParams.from,
                        toSec: periodParams.to,
                        countBack: periodParams.countBack,
                    },
                },
                fetchPolicy,
            });
            // Predictions render a probability line, so the transform consumes the
            // `price` series directly (not full OHLC); `?? []` because TV needs an
            // array even when the outcome has no data yet.
            return predictionPricePointsToBars(result.data?.getChartBars?.data?.price ?? []);
        }
        default:
            throw new Error(`fetchBars: unhandled kind ${parsed.kind}`);
    }
}
/** Shallow copy so live mutation never touches a bar held by the LRU cache. */
function cloneBar(bar) {
    return bar ? { ...bar } : undefined;
}
/** Record the most-recent (highest-time) bar seen for a symbol+resolution. */
function rememberLastBar(store, symbolId, resolution, windowed) {
    const last = windowed[windowed.length - 1];
    if (!last)
        return;
    const key = `${symbolId}|${resolution}`;
    const prev = store.get(key);
    if (!prev || last.time > prev.time)
        store.set(key, last);
}
/** Drop every cached anchor bar for a symbol (all resolutions) on a transition. */
function clearLastBars(store, symbolId) {
    for (const key of store.keys()) {
        if (key.startsWith(`${symbolId}|`))
            store.delete(key);
    }
}
function startApolloSubscription(client, parsed, handleTick) {
    switch (parsed.kind) {
        case TVSymbolKind.TOKEN: {
            const sub = client
                .subscribe({
                query: InstrumentPriceUpdatesSubscription,
                variables: { input: { standardizedInstrumentIds: [parsed.standardizedInstrumentId] } },
            })
                .subscribe({
                next: (result) => {
                    const update = result.data?.instrumentPriceUpdates;
                    if (update?.__typename !== "TokenPriceUpdate")
                        return;
                    if (update.standardizedInstrumentId !== parsed.standardizedInstrumentId)
                        return;
                    const raw = update.currentPrice?.end?.value;
                    if (!notEmpty(raw))
                        return;
                    const price = Number(raw) / BIGINT_SCALE_FACTOR;
                    if (!Number.isFinite(price) || price <= 0)
                        return;
                    const ts = parseTimestampMs(update.currentPrice?.end?.timestamp);
                    // No fill size on token price updates — synthetic volume mirrors
                    // the historical `volume = |close-open| * 1000` in tokenChartSeries.
                    handleTick(price, 0, ts);
                },
            });
            return sub;
        }
        case TVSymbolKind.PERP: {
            const sub = client
                .subscribe({
                query: PerpTradeUpdatesSubscription,
                variables: { input: { standardizedInstrumentId: parsed.standardizedInstrumentId } },
            })
                .subscribe({
                next: (result) => {
                    const trade = result.data?.perpTradeUpdates;
                    if (!trade)
                        return;
                    const rawPrice = trade.price?.value;
                    if (!notEmpty(rawPrice))
                        return;
                    const price = Number(rawPrice) / BIGINT_SCALE_FACTOR;
                    if (!Number.isFinite(price) || price <= 0)
                        return;
                    const rawSize = trade.size?.value;
                    const volume = notEmpty(rawSize) ? Number(rawSize) / BIGINT_SCALE_FACTOR : 0;
                    const ts = parseTimestampMs(trade.timestamp);
                    handleTick(price, Number.isFinite(volume) ? volume : 0, ts);
                },
            });
            return sub;
        }
        case TVSymbolKind.PRED: {
            const sub = client
                .subscribe({
                query: PredictionTradeUpdatesSubscription,
                variables: { input: { standardizedInstrumentId: parsed.standardizedInstrumentId } },
            })
                .subscribe({
                next: (result) => {
                    const trade = result.data?.predictionTradeUpdates;
                    if (!trade)
                        return;
                    if (trade.outcomeTokenId !== parsed.outcomeTokenId)
                        return;
                    const raw = trade.price;
                    if (!notEmpty(raw))
                        return;
                    // Normalize through the same helper as the historical mapper so a
                    // live tick lands on the identical 0–100 scale as the bars it
                    // extends — whether it arrives as a [0, 1] float (`pricePerOutcome`)
                    // or BigInt micro-units.
                    const percent = rawProbabilityToPercent(Number(raw));
                    if (!notEmpty(percent))
                        return;
                    const ts = parseTimestampMs(trade.timestamp);
                    handleTick(percent, 0, ts);
                },
            });
            return sub;
        }
        case TVSymbolKind.INLINE:
            return { unsubscribe: () => undefined };
        default:
            throw new Error(`startApolloSubscription: unhandled kind ${parsed.kind}`);
    }
}
function parseTimestampMs(raw) {
    if (raw instanceof Date)
        return raw.getTime();
    if (typeof raw === "string") {
        const ms = new Date(raw).getTime();
        if (Number.isFinite(ms))
            return ms;
    }
    return Date.now();
}
export const PRICESCALE_BY_KIND = {
    [TVSymbolKind.TOKEN]: TOKEN_DEFAULT_PRICESCALE,
    [TVSymbolKind.PERP]: TOKEN_DEFAULT_PRICESCALE,
    [TVSymbolKind.PRED]: PRED_PRICESCALE,
    [TVSymbolKind.INLINE]: TOKEN_DEFAULT_PRICESCALE,
};
